import styles from './RichBlurb.module.scss';

import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, Document, INLINES, Inline, Node, UnorderedList } from '@contentful/rich-text-types';
import cx from 'classnames';
import React, { FC, HTMLAttributes, ReactHTML, ReactNode } from 'react';

import { Bullet, Image } from 'api/contentful/contentTypes.cloverdotcom';
import { LinkRenderer } from 'components/MarkdownWrapper/LinkRenderer';
import { SubjectImage } from 'components/SubjectImage/SubjectImage';
import { TextV2Variant, variantClassNames } from 'components/TextV2/TextV2';
import { FlatEntry, flattenContentfulData } from 'services/cms/flattenContentful';
import { ParagraphRenderer } from './ParagraphRenderer/ParagraphRenderer';
import { UnorderedListRenderer } from './UnorderedListRenderer/UnorderedListRenderer';

const options = ({ noParagraph }: { noParagraph?: boolean }) => ({
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node: UnorderedList, children: ReactNode) => {
      if (Array.isArray(children) && !children.filter(Boolean).length) {
        return null;
      }

      return (
        <ParagraphRenderer noParagraph={noParagraph} className={styles.paragraph}>
          {children}
        </ParagraphRenderer>
      );
    },
    [BLOCKS.UL_LIST]: (node: any, children: React.ReactNode) => (
      <UnorderedListRenderer className={styles.list}>{children}</UnorderedListRenderer>
    ),
    [INLINES.HYPERLINK]: (node: Inline, children: ReactNode) => (
      <LinkRenderer href={node.data.uri}>{children}</LinkRenderer>
    ),
    [INLINES.EMBEDDED_ENTRY]: (node: Inline) => {
      const flatEntry = flattenContentfulData(node.data.target);
      const contentType = getContentType(node);

      if (contentType === 'bullet') {
        const { icon, noMarginBottom, underline, variant } = flatEntry as FlatEntry<Bullet>;

        return (
          <span
            className={cx(
              styles.bullet,
              icon && styles.icon,
              noMarginBottom && styles.noMarginBottom,
              underline && styles.underline,
              variant && styles[variant],
            )}
          >
            {icon?.file?.url && (
              <img className={styles.iconImage} src={icon.file.url} alt={icon.description || ''} />
            )}
          </span>
        );
      } else if (contentType === 'image') {
        return <SubjectImage entry={flatEntry as FlatEntry<Image>} isLazyLoad={false} />;
      }

      return null;
    },
  },
});

export const RichBlurb: FC<
  {
    document?: Document;
    noMarginGap?: boolean;
    noParagraph?: boolean;
    tagName?: keyof ReactHTML;
    textVariant?: TextV2Variant;
  } & HTMLAttributes<HTMLElement>
> = ({
  document,
  className,
  noMarginGap,
  noParagraph,
  tagName: Tag = 'div',
  textVariant = 'modern-body',
  ...props
}) => {
  if (!document) return null;

  return (
    <Tag
      className={cx(styles.root, variantClassNames[textVariant], !noMarginGap && styles.withGap, className)}
      {...props}
    >
      {documentToReactComponents(document, options({ noParagraph }))}
    </Tag>
  );
};

function getContentType(node: Node) {
  return node.data.target.sys.contentType.sys.id;
}
